import { ReactElement, useState, useEffect, useRef } from "react";
import icon1 from "../../../assets/images&icons/Benefits1.svg";
import icon2 from "../../../assets/images&icons/Benefits2.svg";
import icon3 from "../../../assets/images&icons/Benefits3.svg";
import icon4 from "../../../assets/images&icons/Benefits4.svg";
import "../AboutUs.css";

interface IBreadcrumbItem {
  label: string;
  href: string;
}

const useCountUp = (end: number, duration: number = 2000, suffix: string = "") => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) {
        startTime = currentTime;
      }
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return { count: count + suffix, ref };
};

const CountUp = ({ end, suffix = "", duration = 2000, className = "", style = {} }: {
  end: number;
  suffix?: string;
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const { count, ref } = useCountUp(end, duration, suffix);
  
  return (
    <div ref={ref} className={className} style={style}>
      {count}
    </div>
  );
};

interface IGeneralLayoutProps {
  children?: ReactElement;
  headerBackgroundImage: string;
  breadcrumbItems?: IBreadcrumbItem[];
  title: string;
}

function LayoutForBenefits({
  children,
}: IGeneralLayoutProps): ReactElement {
  return (
      <main className="flex flex-col flex-1 w-full overflow-hidden pb-20">
        <section className={`flex flex-row min-h-[80vh]`}>
          <div className="w-full bg-white z-10 flex flex-col gap-y-10 h-full pb-20 white-page-layout-content">
            <div className="bg-primary bg-dots pt-8 pb-7 flex flex-col justify-center">
              <div className="w-full">
                <div className="general-layout-content">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {/* Section 1 */}
                  <div className="benefits-section">
                    <div className="flex flex-col items-center text-center">
                      <img
                        src={icon1}
                        alt="Benefit 1"
                        className="w-16 h-16 mb-4"
                      />
                      <CountUp 
                        end={150} 
                        suffix="+" 
                        duration={2500}
                        className="benefits-number text-white mb-2"
                      />
                      <p className="benefits-number-title text-white mb-4">
                        Jurisdictions
                      </p>
                      <div className="w-16 h-0.5 bg-white"></div>
                    </div>
                  </div>

                  {/* Section 2 */}
                  <div className="benefits-section">
                    <div className="flex flex-col items-center text-center">
                      <img
                        src={icon2}
                        alt="Benefit 2"
                        className="w-16 h-16 mb-4"
                      />
                      <CountUp 
                        end={23} 
                        suffix="K+" 
                        duration={2500}
                        className="benefits-number text-white mb-2"
                      />
                      <p className="benefits-number-title text-white mb-4">
                        Professionals
                      </p>
                      <div className="w-16 h-0.5 bg-white"></div>
                    </div>
                  </div>

                  {/* Section 3 */}
                  <div className="benefits-section">
                    <div className="flex flex-col items-center text-center">
                      <img
                        src={icon3}
                        alt="Benefit 3"
                        className="w-16 h-16 mb-4"
                      />
                      <CountUp 
                        end={120} 
                        suffix="+" 
                        duration={2500}
                        className="benefits-number text-white mb-2"
                      />
                      <p className="benefits-number-title text-white mb-4">
                        Member Firms
                      </p>
                      <div className="w-16 h-0.5 bg-white"></div>
                    </div>
                  </div>

                  {/* Section 4 */}
                  <div className="benefits-section">
                    <div className="flex flex-col items-center text-center">
                      <img
                        src={icon4}
                        alt="Benefit 4"
                        className="w-16 h-16 mb-4"
                      />
                      <CountUp 
                        end={3} 
                        suffix="" 
                        duration={1500}
                        className="benefits-number text-white mb-2"
                      />
                      <p className="benefits-number-title text-white mb-4">
                        Service Areas
                      </p>
                      <div className="w-16 h-0.5 bg-white"></div>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </div>
            {children}
            <div className="flex flex-row gap-x-2 px-10">
              {Array.from({ length: 3 }, (_, i) => (
                <div
                  key={i}
                  className="bg-[#bc3323] rounded-full w-6 h-6"
                ></div>
              ))}
            </div>
          </div>
        </section>
      </main>
  );
}

export default LayoutForBenefits;
